import React, { useCallback, useEffect, type ChangeEvent } from 'react';
import { styled } from '@compiled/react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Field } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current';
import type { User } from '@atlassian/jira-shared-types';
import { FILE_ENCODING_DROPDOWN_TESTID_PREFIX } from '../../../../common/constants';
import { SUPPORTED_ENCODING } from '../../../../common/constants/encoding';
import { Accordion } from '../../../../common/ui/accordion';
import { InfoButton } from '../../../../common/ui/info-button';
import {
	getLocaleOptionForUserLocale,
	getTimeZoneOptionForUserTimeZone,
} from '../../../../common/utils';
import { useAdvancedOptions, useMappingState } from '../../../../controllers/import-state';
import messages from './messages';

export const UploadAdvancedOptions = () => {
	const { formatMessage } = useIntl();
	const [advancedOptions, { updateAdvancedOptions }] = useAdvancedOptions();
	const [mappingState, { setMappingState }] = useMappingState();

	const {
		data: { user },
	} = useCurrentUser();

	useEffect(() => {
		const localeOption = getLocaleOptionForUserLocale(user?.locale);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const timeZoneOption = getTimeZoneOptionForUserTimeZone((user as User)?.timeZone);
		updateAdvancedOptions({ localeOption, timeZoneOption });

		// Only run if user changes
	}, [updateAdvancedOptions, user]);

	const getOnChangeFileEncoding = useCallback(
		(fileEncoding: string) => () => {
			updateAdvancedOptions({ fileEncoding });

			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}
		},
		[mappingState, updateAdvancedOptions, setMappingState],
	);

	const onChangeDelimiter = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			updateAdvancedOptions({ delimiter: e.target.value });

			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}
		},
		[mappingState, updateAdvancedOptions, setMappingState],
	);

	return (
		<Wrapper>
			<Accordion
				heading={messages.advancedOptions}
				testId="project-create-importer-upload-flow.ui.upload-screen.upload-csv-dropzone.upload-advanced-options.accordion"
				ariaRoleDescription={messages.advancedOptionsDescription}
			>
				<Field
					label={
						<LabelWrapper>
							{formatMessage(messages.fileEncoding)}
							<InfoButton
								heading={formatMessage(messages.fileEncodingPopupHeading)}
								description={formatMessage(messages.fileEncodingPopupDescription)}
							/>
						</LabelWrapper>
					}
					name="fileEncoding"
				>
					{({ fieldProps }) => (
						<DropdownWrapper data-testid="project-create-importer-upload-flow.ui.upload-screen.upload-csv-dropzone.upload-advanced-options.dropdown-wrapper">
							<DropdownMenu {...fieldProps} trigger={advancedOptions.fileEncoding}>
								<DropdownItemGroup>
									{SUPPORTED_ENCODING.map((encoding) => (
										<DropdownItem
											key={encoding}
											onClick={getOnChangeFileEncoding(encoding)}
											testId={`${FILE_ENCODING_DROPDOWN_TESTID_PREFIX}.${encoding}`}
										>
											{encoding}
										</DropdownItem>
									))}
								</DropdownItemGroup>
							</DropdownMenu>
						</DropdownWrapper>
					)}
				</Field>
				<Box xcss={spacerStyles} />
				<Field
					label={
						<LabelWrapper>
							{formatMessage(messages.delimiter)}
							<InfoButton
								heading={formatMessage(messages.delimiterPopupHeading)}
								description={formatMessage(messages.delimiterPopupDescription)}
							/>
						</LabelWrapper>
					}
					name="delimiter"
				>
					{({ fieldProps }) => (
						<>
							<TextField
								testId="project-create-importer-upload-flow.ui.upload-screen.upload-csv-dropzone.upload-advanced-options.text-field"
								{...fieldProps}
								defaultValue={advancedOptions.delimiter}
								onChange={onChangeDelimiter}
							/>
						</>
					)}
				</Field>
			</Accordion>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	maxWidth: '60%',
});

const spacerStyles = xcss({
	width: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		marginLeft: token('space.050', '4px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		height: token('space.500', '40px'),
		width: '136px',
		alignItems: 'center',
	},
});
